<template>
	<v-card>
		<v-card-title class="d-flex align-center justify-space-between">
			{{ dialogTitle }}
			<v-btn icon variant="flat" @click="closeDialog">
				<v-icon> mdi-close </v-icon>
			</v-btn>
		</v-card-title>
		<v-container style="width: 95%" class="my-2 mx-auto">
			<v-row>
				{{ t("SELECT_SINGLE_DATASET_FILE") }}
				<span v-if="allowedExtensionsLabel" class="extensions-label">({{ allowedExtensionsLabel }})</span>
			</v-row>
			<v-row class="mt-3 mb-3 justify-center">
				<v-col cols="12" class="groupware-block">
					<v-row :class="selectedFileName ? 'mb-3' : ''">
						<v-col class="pa-0">
							<v-btn variant="flat" class="groupware-btn" rounded @click="onPrimaryActionClick">
								{{ primaryActionLabel }}
							</v-btn>
							<input v-if="isLocalSelectionMode" ref="localFileInput" type="file" multiple
								:accept="allowedFileTypes" class="hidden-local-file-input" @change="onLocalFileSelected">
						</v-col>
					</v-row>
					<v-row v-if="selectedFileName" class="groupware-file-row mt-2 align-center no-gutters">
						<span class="selected-file-name groupware-file-name">{{ selectedFileNames }}</span>
						<v-btn icon variant="text" density="compact" class="remove-file-btn" @click="clearSelectedFile">
							<v-icon size="18">mdi-close</v-icon>
						</v-btn>
					</v-row>
				</v-col>
			</v-row>
			<v-row v-if="errorMessage" class="error-text">{{ errorMessage }}</v-row>
			<v-row>
				<v-col class="d-flex justify-end">
					<v-btn color="primary" class="uploadBtn" :disabled="!selectedFile" rounded @click="emitConfirm">
						{{ t("NEXT") }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
	FILE_SELECTION_ERROR_KEYS,
	resolveFileSelection,
} from "@/components/AddData/fileSelectionResolver";
import {
	type UploadDataType,
	type UploadSource,
	resolveUploadTypeByExtension,
	uploadSources,
} from "@/components/AddData/uploadTypeResolver";

type PreselectedFile = Readonly<{
	name: string;
}>;

type SelectableFile = File | PreselectedFile;

const props = defineProps<{
	allowedFileTypes: string;
	source?: UploadSource;
	preselectedFile?: PreselectedFile;
}>();

const emit = defineEmits<{
	(e: "close"): void;
	(e: "confirm", file: SelectableFile, resolvedType: UploadDataType): void;
	(e: "confirmTiffPair", files: readonly [File, File]): void;
	(e: "requestGroupwareReselection"): void;
}>();

const { t } = useI18n();
// 選択したファイル（選択順を保持。tiff ペアのときだけ 2 件になる）
const selectedFiles = ref<readonly SelectableFile[]>(
	props.preselectedFile ? [props.preselectedFile] : [],
);
const localFileInput = ref<HTMLInputElement | null>(null);
const errorMessage = ref("");
const selectedFile = computed<SelectableFile | null>(
	() => selectedFiles.value[0] ?? null,
);

const source = computed(() => props.source ?? uploadSources.local);
const isGroupwareSource = computed(
	() => source.value === uploadSources.groupware,
);
const isLocalSelectionMode = computed(
	() => !isGroupwareSource.value || !props.preselectedFile,
);
const dialogTitle = computed(() =>
	isGroupwareSource.value ? t("IMPORT_FROM_GROUPWARE") : t("FILE_UPLOAD"),
);
const selectedFileName = computed(() => selectedFile.value?.name ?? "");
const selectedFileNames = computed(() =>
	selectedFiles.value.map((file) => file.name).join(", "),
);
const primaryActionLabel = computed(() =>
	isLocalSelectionMode.value ? t("SELECT_FILES") : t("IMPORT_FROM_GROUPWARE"),
);
const allowedExtensionsLabel = computed(() =>
	props.allowedFileTypes
		.split(",")
		.map((item) => item.trim())
		.filter((item) => item.length > 0)
		.map((item) => (item.startsWith(".") ? item.slice(1) : item))
		.join(", "),
);

watch(
	() => props.preselectedFile,
	(nextFile) => {
		selectedFiles.value = nextFile ? [nextFile] : [];
		errorMessage.value = "";
	},
);

const closeDialog = () => {
	errorMessage.value = "";
	selectedFiles.value = [];
	emit("close");
};

const openLocalFilePicker = () => {
	errorMessage.value = "";
	localFileInput.value?.click();
};

const onPrimaryActionClick = () => {
	if (isLocalSelectionMode.value) {
		openLocalFilePicker();
		return;
	}

	requestGroupwareReselection();
};

const onLocalFileSelected = (event: Event) => {
	errorMessage.value = "";
	const target = event.target;
	if (!(target instanceof HTMLInputElement)) {
		return;
	}
	const nextFiles = Array.from(target.files ?? []);
	if (nextFiles.length === 0) {
		return;
	}
	selectedFiles.value = nextFiles;
	// 同じファイル（群）を選び直しても change が発火するよう選択値を捨てる。
	// 枚数エラー（AC-4）のあと同じセットを選び直す導線で、前回の選択が残らないようにする
	target.value = "";
};

const requestGroupwareReselection = () => {
	errorMessage.value = "";
	emit("requestGroupwareReselection");
};

const clearSelectedFile = () => {
	errorMessage.value = "";
	selectedFiles.value = [];
	if (localFileInput.value) {
		localFileInput.value.value = "";
	}
};

/** 選択が実ファイルのみか（Groupware の preselect は { name } しか持たない） */
const isFileSelection = (
	files: readonly SelectableFile[],
): files is readonly File[] => files.every((file) => file instanceof File);

const emitConfirm = () => {
	const files = selectedFiles.value;
	if (files.length === 0) {
		return;
	}

	// Groupware の preselect は File ではなく { name } のため、従来どおり拡張子だけで判定する
	if (!isFileSelection(files)) {
		const resolvedType = resolveUploadTypeByExtension(
			files[0].name,
			source.value,
		);
		if (!resolvedType) {
			errorMessage.value = t("FILE_TYPE_NOT_SUPPORTED");
			return;
		}
		emit("confirm", files[0], resolvedType);
		return;
	}

	const resolution = resolveFileSelection(files, source.value);
	if (resolution.kind === "invalid") {
		errorMessage.value = t(FILE_SELECTION_ERROR_KEYS[resolution.reason]);
		return;
	}
	if (resolution.kind === "tiffPair") {
		emit("confirmTiffPair", resolution.files);
		return;
	}
	emit("confirm", resolution.file, resolution.resolvedType);
};
</script>

<style scoped>
.hidden-local-file-input {
	display: none;
}

.uploadBtn {
	margin-bottom: 3%;
}

.groupware-btn {
	background-color: #565664;
	color: #ffffff;
}

.groupware-block {
	border: 1px dashed rgba(151, 143, 143, 0.8);
	border-radius: 4px;
	padding: 25px 8px 25px 25px;
}

.groupware-file-row {
	width: 100%;
	padding-left: 20px;
	display: flex;
	align-items: center;
}

.remove-file-btn {
	margin-left: auto;
	flex-shrink: 0;
	margin-right: 4px;
}

.groupware-file-name {
	padding-right: 12px;
}

.error-text {
	color: #ff5252;
}

.selected-file-name {
	word-break: break-all;
}

.extensions-label {
	margin-left: 8px;
}
</style>
